import React, { useCallback, useMemo, useState, useEffect } from "react";
import { useSelector } from "react-redux";
import { useParams } from "react-router-dom";

import VerticalSpacing from "@cayuse/shared-react.components.vertical-spacing";
import { isNonEmptyString } from "@cayuse/shared-react.utils.type-checks";
import PropTypes from "prop-types";

import FormResponse from "components/FormResponse";
import Modifications from "containers/Modifications";
import {
	SUBAWARD_REVIEW_STATUS,
	COLUMN_HEADER_KEYS,
	ROLE_SP_SYSTEM_ADMINISTRATOR,
	DEFAULT_SUBAWARD_COLUMN_HEADERS,
	API_AWARD,
	SUBAWARDS,
	MODIFICATION_STATUS,
	ROLE_SP_SUBAWARD_ADMINISTRATOR,
	ROLE_SP_SUBAWARD_EDITOR,
	API_SUBAWARD,
} from "globals/constants";
import { useColumnHeaders, useDictionary, useFormAccesses, useHasRoles, useRecordRevisionAccess } from "hooks";
import { getSubaward, getSubawardValues, updateSubawardOrProperty } from "store/actions/subaward";
import { modificationListSelectors, subawardSelectors } from "store/selectors";

import { getFormModifications, refreshFormModifications } from "../../../../store/actions/modifications";
import { getCaseInstanceId, spUrl } from "../../../../utils";


const SUBAWARD_LIST_DEFAULT_COLUMNS = [
	DEFAULT_SUBAWARD_COLUMN_HEADERS.SUBAWARD_NUMBER,
	DEFAULT_SUBAWARD_COLUMN_HEADERS.STATUS,
	DEFAULT_SUBAWARD_COLUMN_HEADERS.SUBAWARDEE,
	DEFAULT_SUBAWARD_COLUMN_HEADERS.SUBAWARD_BEGIN_DATE,
	DEFAULT_SUBAWARD_COLUMN_HEADERS.SUBAWARD_END_DATE,
	DEFAULT_SUBAWARD_COLUMN_HEADERS.TOTAL_EXPECTED,
	DEFAULT_SUBAWARD_COLUMN_HEADERS.PRIMARY_ADMIN_CONTACT,
	DEFAULT_SUBAWARD_COLUMN_HEADERS.PRIMARY_CONTACT_EMAIL,
	DEFAULT_SUBAWARD_COLUMN_HEADERS.RESOLUTION
];

const SubawardFormView = props => {
	const { subaward } = useSelector(subawardSelectors.content);
	const formAccesses = useFormAccesses("subaward", subaward.subawardId);
	const dictionary = useDictionary("common.form", { sectionTitle: "Subaward Sections" });
	const { awardId, subawardId } = useParams();
	const [ currentModification, setCurrentModification ] = useState({});
	const [ isModificationChange, setIsModificationChange ] = useState(false);
	const [ shouldUpdateForm, setShouldUpdateForm ] = useState(false);

	const {
		[ROLE_SP_SUBAWARD_ADMINISTRATOR]: isSubawardAdmin,
		[ROLE_SP_SYSTEM_ADMINISTRATOR]: isSystemAdmin,
		[ROLE_SP_SUBAWARD_EDITOR]: isSubawardEditor,
	} = useHasRoles([
		ROLE_SP_SUBAWARD_ADMINISTRATOR,
		ROLE_SP_SYSTEM_ADMINISTRATOR,
		ROLE_SP_SUBAWARD_EDITOR,
	]);

	const { columnHeaders: availableColumns } = useColumnHeaders(COLUMN_HEADER_KEYS.SUBAWARD, {
		defaultColumnHeaders: SUBAWARD_LIST_DEFAULT_COLUMNS
	});

	const { latestModificationNumber = 0 } = useSelector(
		modificationListSelectors.content
	);

	const isLatestModification = parseInt(currentModification.modificationNumber) === parseInt(latestModificationNumber);

	const canEdit = useMemo(() => {
		const hasEditPermission = isSubawardAdmin || isSubawardEditor || isSystemAdmin;
		const canModify = [
			SUBAWARD_REVIEW_STATUS.active, SUBAWARD_REVIEW_STATUS.closeout,
		].includes(subaward.statusName) &&
			currentModification.modificationStatus === MODIFICATION_STATUS.draft &&
			hasEditPermission;

		const isEditableStatus = ([ SUBAWARD_REVIEW_STATUS.setup, SUBAWARD_REVIEW_STATUS.inReview ].includes(subaward.statusName)
      && (isLatestModification || subaward.modificationNumber === null)) || canModify;

		return formAccesses.updateForm && isEditableStatus;
	}, [
		currentModification.modificationStatus,
		subaward.statusName,
		formAccesses.updateForm,
		isSubawardAdmin,
		isSubawardEditor,
		isSystemAdmin,
		isLatestModification,
	]);

	const disableEditButtons = useMemo(() => {
		return !formAccesses.systemAdmin && subaward.statusName === SUBAWARD_REVIEW_STATUS.closeout;
	}, [ formAccesses, subaward ]);

	//const hasRecordRevisionEditAccess = useRecordRevisionAccess(COLUMN_HEADER_KEYS.SUBAWARD, subaward.statusName, {updateForm: isAwardAdmin || isSystemAdmin});
	const hasRecordRevisionEditAccess = useRecordRevisionAccess(
		COLUMN_HEADER_KEYS.SUBAWARD,
		subaward.statusName,
		formAccesses,
		isLatestModification
	) && (currentModification.modificationStatus !== MODIFICATION_STATUS.draft);
	const onFormSave = useCallback(() => {
		getSubawardValues({
			awardId: subaward.awardId,
			subawardId: subaward.subawardId,
		});
		refreshFormModifications();
	}, [ subaward.awardId, subaward.subawardId ]);

	useEffect(() => {
		const fetchData = async () => {
			const formInfo = {
				formId: subawardId,
				isSubaward: true,
			};
			const response = await getFormModifications({ formInfo });
			const caseInstanceId = getCaseInstanceId(response.content);
			await getSubaward(subawardId, { caseInstanceId, isRefresh: false });
		};

		fetchData();
	}, [ subawardId ]);

	useEffect(() => {
		const subawardModificationNumber = subaward.modificationNumber === null ? 0 : subaward.modificationNumber;
		if (
			isNonEmptyString(currentModification.caseInstanceId) &&
			currentModification.modificationNumber != subawardModificationNumber &&
			subaward.formElements
		) {
			updateSubawardOrProperty({
				...subaward,
				...currentModification,
				adminTeamMembers: subaward.adminTeamMembers,
			});

			setShouldUpdateForm(true);
		}
	}, [ subaward, currentModification ]);

	return (
		<>
			<VerticalSpacing>
				<Modifications
					key={subawardId}
					currentModification={currentModification}
					caseInstanceId={
						isNonEmptyString(currentModification.caseInstanceId)
							? currentModification.caseInstanceId
							: subaward.caseInstanceId
					}
					formType={COLUMN_HEADER_KEYS.SUBAWARD}
					formId={subaward.subawardId}
					formApi={`${API_SUBAWARD}/${subaward.subawardId}`}
					formStatus={subaward.statusName}
					isFormAdmin={formAccesses.manage || formAccesses.editor}
					canReroute={formAccesses.changeStatus}
					setCurrentModification={setCurrentModification}
					setIsModificationChange={setIsModificationChange}
					availabletModificationsColumns={availableColumns}
				/>
			</VerticalSpacing>
			<FormResponse
				form={subaward}
				canEdit={canEdit}
				dictionary={dictionary}
				formIdKey="subawardId"
				formType={COLUMN_HEADER_KEYS.SUBAWARD}
				isFormAdmin={isSubawardAdmin || isSystemAdmin || false}
				loadedFormId={subawardId}
				onFlowableFormSave={onFormSave}
				payloadRootContentKey={"subawardContent"}
				saveOnChange={true}
				saveValuesApiUrl={spUrl(`${API_SUBAWARD}/${subaward.subawardId}`)}
				saveValuesRecordRevisionApiUrl={spUrl(`${API_SUBAWARD}/${subaward.subawardId}`)}
				statusId={subaward.statusKey}
				recordRevisionEditMode={hasRecordRevisionEditAccess}
				editLock={subaward.editLock}
				setIsModificationChange={setIsModificationChange}
				isModificationChange={isModificationChange}
				setShouldUpdateForm={setShouldUpdateForm}
				shouldUpdateForm={shouldUpdateForm}
				disableEditButtons={disableEditButtons}
				{...props}
			/>
		</>
	);
};

SubawardFormView.propTypes = {
	handleFormLoaded: PropTypes.func,
	setFormVariables: PropTypes.func,
	formVariables: PropTypes.object,
};

export default SubawardFormView;
